/**
 * phoneNumber.ts
 *
 * One shared rule for the phone numbers people type on BotSpot forms
 * (Rob, 2026-10-19). Production challenge signups since Sep 1 stored 20 of
 * 142 phones in some non-E.164 shape (ten bare digits, 1 plus ten digits,
 * placeholders), which makes texting and matching people harder.
 *
 * WHY THIS EXISTS:
 * - Parse with the same library, version (1.13.13), metadata (max), default
 *   country (US) and placeholder list as botspot_node
 *   src/messaging/phoneIdentity.ts, so "valid" here means valid on the server.
 * - Phone is optional everywhere it is optional today. Nothing here blocks a
 *   form. An unparseable number is returned exactly as typed (trimmed) so the
 *   generic path never drops what someone entered.
 *
 * IMPORTANT BEHAVIORS:
 * - The library (about 40 KB gzipped) loads lazily, off the landing page's
 *   critical path. Until it arrives, US/Canada ten and eleven digit numbers
 *   still normalize through a small built-in rule, and everything else is
 *   left as typed.
 * - Display formatting only tidies a valid number.
 */
import type * as PhoneLibraryModule from 'libphonenumber-js/max';

type PhoneLibrary = typeof PhoneLibraryModule;

let phoneLibrary: PhoneLibrary | null = null;
let pendingLoad: Promise<PhoneLibrary | null> | null = null;

/** Starts (or reuses) the lazy load. Resolves null if the chunk cannot load. */
export function loadPhoneLibrary(): Promise<PhoneLibrary | null> {
  if (phoneLibrary) return Promise.resolve(phoneLibrary);
  if (!pendingLoad) {
    pendingLoad = import(/* webpackChunkName: "phone-number" */ 'libphonenumber-js/max')
      .then((module) => {
        phoneLibrary = module;
        return module;
      })
      .catch(() => {
        // A failed chunk must never break a form. Allow a later retry.
        pendingLoad = null;
        return null;
      });
  }
  return pendingLoad;
}

export const isPhoneLibraryLoaded = (): boolean => phoneLibrary !== null;

// Same list the server refuses (botspot_node phoneIdentity.ts).
const PLACEHOLDER_E164 = new Set(['+15555551234', '+19999999999']);
const PHONE_CHARACTERS = /^[+\d\s().-]+$/;
const NANP_TEN_DIGITS = /^[2-9]\d{2}[2-9]\d{6}$/;
const NANP_ELEVEN_DIGITS = /^1[2-9]\d{2}[2-9]\d{6}$/;

/** What a phone field can hold: typed text, a saved value, or nothing yet. */
export type PhoneText = string | null | undefined;

const asText = (value: PhoneText): string => (value == null ? '' : String(value));

/** Used only before the library has loaded: US/Canada shapes, nothing else. */
const fallbackE164 = (trimmed: string): string | null => {
  if (trimmed.startsWith('+') || !PHONE_CHARACTERS.test(trimmed)) return null;
  const digits = trimmed.replace(/\D/g, '');
  if (/^(.)\1+$/.test(digits)) return null;
  let e164: string | null = null;
  if (NANP_TEN_DIGITS.test(digits)) e164 = `+1${digits}`;
  else if (NANP_ELEVEN_DIGITS.test(digits)) e164 = `+${digits}`;
  return e164 && !PLACEHOLDER_E164.has(e164) ? e164 : null;
};

/** E.164 (for example +14165551234) when the number is valid, otherwise null. */
export function toValidE164(value: PhoneText): string | null {
  const trimmed = asText(value).trim();
  if (!trimmed) return null;
  if (!phoneLibrary) return fallbackE164(trimmed);
  try {
    const parsed = phoneLibrary.parsePhoneNumberWithError(trimmed, {
      defaultCountry: trimmed.startsWith('+') ? undefined : 'US',
      extract: false,
    });
    if (!parsed.isValid() || PLACEHOLDER_E164.has(parsed.number)) return null;
    return parsed.number;
  } catch {
    return null;
  }
}

/**
 * What a form should send: E.164 when parseable, otherwise exactly what was
 * typed (trimmed). Empty stays empty. Never throws, never drops input.
 */
export function normalizePhoneForSubmit(value: PhoneText): string {
  const trimmed = asText(value).trim();
  if (!trimmed) return '';
  return toValidE164(trimmed) ?? trimmed;
}

/**
 * Tidy display for a finished entry (on blur, or a saved value): national
 * format for +1 numbers, international otherwise. Unparseable input is shown
 * exactly as typed.
 */
export function formatPhoneForDisplay(value: PhoneText): string {
  const text = asText(value);
  const e164 = toValidE164(text);
  if (!e164 || !phoneLibrary) return text;
  try {
    const parsed = phoneLibrary.parsePhoneNumber(e164);
    return parsed.countryCallingCode === '1'
      ? parsed.formatNational()
      : parsed.formatInternational();
  } catch {
    return text;
  }
}
